import { useEffect, useRef, useState } from 'react'

import { STUDY_EVENTS } from '../constants/telemetryEvents'
import { aiUnlockAtMs, getStudyConfig } from './studyConfig'
import { useServerOffset } from './useServerNow'

// A delayed study condition's AI chat (A5-A8). The research lead's decision: no chat column at
// all from the experiment's Start until aiDelaySeconds later - no locked panel, no countdown;
// until then the page looks like the No-AI condition's - and then the column appears. The
// moment is on the server's clock (started_at is written with it), so neither a refresh nor a
// student's wrong computer clock shows the chat early or restarts the wait. Only the page
// enforces it. Staff opening a condition see what its students see.
//
// Workspace re-renders for this only when something changes: one timer, set for the unlock
// moment and set again when started_at or the clock's offset changes - not a tick a second.

// How long a study condition's page waits for the server's clock before it logs AI_AVAILABILITY
// anyway (serverClock: false). A delayed condition's chat goes by the server's clock only
// (useServerNow keeps trying until it answers): a computer clock that runs fast must never
// show it early, and a column shown early and then hidden again loses the student's draft.
export const SERVER_CLOCK_WAIT_MS = 10000

// setTimeout's longest wait (about 24.8 days); a longer one would fire at once.
const MAX_TIMEOUT_MS = 2147483647

// { locked, unlockAtMs, clockKnown }:
//   locked      the AI chat column must not show (only ever for a delayed condition: before
//               Start, before its time, and while the page has no settled clock)
//   unlockAtMs  when a delayed condition's chat opens (ms since 1970); null before the
//               experiment's Start and for every other session
//   clockKnown  whether the page has the server's clock yet
// Every other session is never locked and measures nothing.
//
// It logs the study's AI telemetry too, for every study condition: AI_AVAILABILITY once per
// page, once its clock is settled (the server's, or the student's after SERVER_CLOCK_WAIT_MS),
// and AI_UNLOCKED when a delayed condition's column appears because its time came while the
// page was open. chatAllowed: whether the session shows the AI chat at all (false for the
// No-AI condition), so that the events say what was on screen.
export const useAiGate = (session, { chatAllowed = true, logTelemetryEvent } = {}) => {
  const sessionId = session?.id
  // Once a study condition, always one for this page (as in useStudyPhase): a session update
  // that came without test_code must not show a delayed condition's chat early.
  const studyRef = useRef(null)
  const seenStudy = getStudyConfig(session)
  if (seenStudy) studyRef.current = { sessionId, study: seenStudy }
  const study = seenStudy || (studyRef.current?.sessionId === sessionId ? studyRef.current.study : null)
  const isStudy = study !== null
  const aiDelaySeconds = isStudy ? study.aiDelaySeconds : 0
  const delayed = aiDelaySeconds > 0
  const unlockAtMs = delayed ? aiUnlockAtMs(session, study) : null

  // Every study condition reads the server's clock (useStudyPhase measures it for the same page
  // anyway): the availability event says which clock the page went by.
  const { offsetMs, known } = useServerOffset(isStudy)
  const [clockWaitOver, setClockWaitOver] = useState(false)
  useEffect(() => {
    if (!isStudy || known) return undefined
    const timer = setTimeout(() => setClockWaitOver(true), SERVER_CLOCK_WAIT_MS)
    return () => clearTimeout(timer)
  }, [isStudy, known])
  const clockSettled = known || clockWaitOver

  // The unlock time this page has seen pass. The chat then stays for that time, through a
  // slightly better clock measurement, a pause or the end of the coding time; a later
  // started_at (a resume before the unlock) sets a new time to wait for.
  const [passedAt, setPassedAt] = useState(null)
  const passedSeen = passedAt !== null && passedAt === unlockAtMs
  // The time only runs while the experiment does: a pause across the unlock moment does not
  // show the chat (and its Resume moves started_at, so the moment moves with it).
  const running = session?.status === 'active'
  const reached = unlockAtMs !== null && (passedSeen || (running && Date.now() + offsetMs >= unlockAtMs))
  const locked = delayed && (!known || !reached)

  // The one timer. When it fires it reads the clock again and waits for the rest if it came a
  // moment early, so the chat cannot stay hidden past its time.
  const waiting = delayed && known && running && unlockAtMs !== null && !passedSeen
  useEffect(() => {
    if (!waiting) return undefined
    let timer = null
    const check = () => {
      const msLeft = unlockAtMs - (Date.now() + offsetMs)
      if (msLeft > 0) timer = setTimeout(check, Math.min(msLeft, MAX_TIMEOUT_MS))
      else setPassedAt(unlockAtMs)
    }
    check()
    return () => clearTimeout(timer)
  }, [waiting, unlockAtMs, offsetMs])

  const panelShown = Boolean(chatAllowed) && !locked

  // AI_AVAILABILITY: what the page showed once its clock was settled, once per page and session.
  const availabilityLoggedFor = useRef(null)
  useEffect(() => {
    if (!isStudy || !clockSettled || availabilityLoggedFor.current === sessionId) return
    availabilityLoggedFor.current = sessionId
    logTelemetryEvent?.(STUDY_EVENTS.AI_AVAILABILITY, {
      panelShown,
      // The earlier payload's field (the locked-panel version), kept for queries written on it.
      locked: locked,
      unlockAtMs,
      aiDelaySeconds,
      serverClock: known,
    })
  }, [isStudy, clockSettled, sessionId, panelShown, locked, unlockAtMs, aiDelaySeconds, known, logTelemetryEvent])

  // AI_UNLOCKED: the column appearing after the page had it hidden while waiting for this
  // unlock time on the server's clock - not on a page that found the time already past (its
  // availability event says the column was shown). Once per unlock time: a later started_at
  // (a resume before the unlock) hides the column again, and its opening is logged again.
  const waitedFor = useRef(null)
  const unlockLogged = useRef(null)
  useEffect(() => {
    if (!delayed || !known || unlockAtMs === null) return
    if (locked) {
      waitedFor.current = unlockAtMs
      return
    }
    if (waitedFor.current !== unlockAtMs) return
    waitedFor.current = null
    if (!chatAllowed || unlockLogged.current === unlockAtMs) return
    unlockLogged.current = unlockAtMs
    logTelemetryEvent?.(STUDY_EVENTS.AI_UNLOCKED, {
      unlockAtMs,
      serverNowMs: Date.now() + offsetMs,
      serverClock: known,
    })
  }, [delayed, unlockAtMs, locked, chatAllowed, offsetMs, known, logTelemetryEvent])

  return { locked, unlockAtMs, clockKnown: known }
}
